import { RestError } from '@azure/core-http';
import { useImmer } from 'use-immer';
import { useMutationArchiveDelivery } from '../../deliveries-details.queries';
import { Delivery } from '../../../models/delivery.model';
import { useMemo } from 'react';
import { KnownDeliveryState } from '@groundfloor-platform/mgmt-service-g-sdk';
import { generateAcDeliveryDetailsUrl } from '../../deliveries-details-actions-observer';

export interface ArchiveDeliveryActionProps {
  delivery: Delivery;
  onAbort(): void;
  onSuccess(): void;
}

export interface ArchiveDeliveryActionState {
  current: string;
  error?: RestError;
  loading: boolean;
}

interface IntentToArchive extends ArchiveDeliveryActionState {
  current: 'ARCHIVE_REQUEST_PENDING';
}

interface ArchiveRequestSuccess extends ArchiveDeliveryActionState {
  current: 'ARCHIVE_REQUEST_SUCCESS';
}

interface ArchiveRequestFailed extends ArchiveDeliveryActionState {
  current: 'ARCHIVE_REQUEST_FAILED';
}

interface ArchiveRequestACMigration extends ArchiveDeliveryActionState {
  current: 'ARCHIVE_REQUEST_AC_MIGRATION';
}

export interface ArchiveDeliveryActionHandlers {
  onCancelIntent(): void;
  onConfirmArchiveDelivery(): void;
  onSuccessArchiveDelivery(): void;
  onOpenLinkToAcDelivery(): void;
}

export type ArchiveDeliveryActionStates =
  | IntentToArchive
  | ArchiveRequestSuccess
  | ArchiveRequestFailed
  | ArchiveRequestACMigration;

export function getInitialState(delivery: Delivery): ArchiveDeliveryActionStates {
  return {
    current: delivery.migratedFromAcDeliveryId ? 'ARCHIVE_REQUEST_AC_MIGRATION' : 'ARCHIVE_REQUEST_PENDING',
    loading: false
  };
}

export function useArchiveDeliveryAction({ onAbort, onSuccess, delivery }: ArchiveDeliveryActionProps) {
  const [state, setState] = useImmer<ArchiveDeliveryActionStates>(getInitialState(delivery));
  const archiveDeliveryMutation = useMutationArchiveDelivery(delivery.id);

  function getIntentSentState(error?: RestError): IntentToArchive {
    return { current: 'ARCHIVE_REQUEST_PENDING', loading: true, error };
  }

  function getArchiveRequestSuccessState(): ArchiveRequestSuccess {
    return { current: 'ARCHIVE_REQUEST_SUCCESS', loading: false };
  }

  function getArchiveRequestFailedState(error: RestError): ArchiveRequestFailed {
    return { current: 'ARCHIVE_REQUEST_FAILED', error, loading: false };
  }

  const handlers: ArchiveDeliveryActionHandlers = {
    onCancelIntent() {
      switch (state.current) {
        case 'ARCHIVE_REQUEST_PENDING':
        case 'ARCHIVE_REQUEST_FAILED':
          if (!state.loading) {
            onAbort();
          }
          break;
        default:
          return;
      }
    },
    onConfirmArchiveDelivery() {
      switch (state.current) {
        case 'ARCHIVE_REQUEST_PENDING':
          setState(getIntentSentState(state.error));
          archiveDeliveryMutation.mutate(undefined, {
            onSuccess() {
              setState(getArchiveRequestSuccessState());
            },
            onError(error) {
              setState(getArchiveRequestFailedState(error));
            }
          });
          break;
        default:
          return;
      }
    },
    onSuccessArchiveDelivery() {
      onSuccess();
    },
    onOpenLinkToAcDelivery() {
      window.open(generateAcDeliveryDetailsUrl(delivery));
      onAbort();
    }
  };

  const canArchive = useMemo(() => {
    return (
      [
        KnownDeliveryState.Abandoned,
        KnownDeliveryState.Collected,
        KnownDeliveryState.Cancelled,
        KnownDeliveryState.Retrieved
      ].includes(delivery.state as KnownDeliveryState) && delivery.migratedFromAcDeliveryId === undefined
    );
  }, [delivery.migratedFromAcDeliveryId, delivery.state]);

  const isMigrated = delivery.migratedFromAcDeliveryId !== undefined;

  return [state, handlers, canArchive, isMigrated] as const;
}
